const API = "https://www.lianfoong.com/api/name-search";
const abortError = () => Object.assign(new Error("Screening cancelled"), { name: "AbortError" });
export const validDob = (value) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value && date <= new Date();
};
const delay = (signal) => new Promise((resolve, reject) => {
  if (signal.aborted) { reject(abortError()); return; }
  const cancel = () => { clearTimeout(timer); signal.removeEventListener("abort", cancel); reject(abortError()); };
  const timer = setTimeout(() => { signal.removeEventListener("abort", cancel); resolve(); }, 1500);
  signal.addEventListener("abort", cancel, { once: true });
});
export async function screenPerson(name, dob, signal) {
  const read = async (url, options = {}) => {
    const response = await fetch(url, { ...options, cache: "no-store", signal,
      headers: { Accept: "application/json", ...options.headers } });
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || "Terrorist/PEP screening could not complete. Please retry or check manually.");
    return { response, data };
  };
  let { response, data } = await read(`${API}/jobs`, { method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, ...(validDob(dob) ? { dob } : {}) }) });
  if (response.status !== 202 || typeof data?.jobId !== "string" || !/^[0-9a-f-]{36}$/i.test(data.jobId))
    throw new Error("Screening could not start. Please deploy the matching backend update.");
  const id = data.jobId;
  while (response.status === 202) {
    await delay(signal);
    ({ response, data } = await read(`${API}/jobs/${encodeURIComponent(id)}`));
  }
  const statuses = ["POSSIBLE_MATCH", "NO_MATCH_FOUND", "UNABLE_TO_CHECK"];
  if (!data || !statuses.includes(data.status) || !statuses.includes(data.terrorismStatus)
      || !statuses.includes(data.pepStatus) || !Array.isArray(data.matches))
    throw new Error("Incomplete terrorist/PEP screening response. Please retry or check manually.");
  if ((data.terrorismStatus === "POSSIBLE_MATCH" && !data.matches.some(m => m.category === "TERRORIST_LIST"))
      || (data.pepStatus === "POSSIBLE_MATCH" && !data.matches.some(m => ["PEP", "PEP_FAMILY", "PEP_ASSOCIATE"].includes(m.category))))
    throw new Error("Screening match details are missing. Please check manually.");
  return data;
}
export const screeningMessage = (result) => {
  const parts = [];
  const matches = Array.isArray(result?.matches) ? result.matches : [];
  if (result?.terrorismStatus === "POSSIBLE_MATCH" || matches.some(m => m?.category === "TERRORIST_LIST")) parts.push("Possible Terrorist List match — verify identity and follow SOP.");
  if (result?.pepStatus === "POSSIBLE_MATCH" || matches.some(m => ["PEP", "PEP_FAMILY", "PEP_ASSOCIATE"].includes(m?.category))) parts.push("Possible PEP / family / close-associate match — verify identity and refer to your manager. PEP status does not imply wrongdoing.");
  return parts.join("\n");
};
// Display each completed check independently. Missing results never become a clear result.
export const nameSearchDisplay = (result, localCheck) => {
  const matches = Array.isArray(result?.matches) ? result.matches : [];
  const terroristMatch = result?.terrorismStatus === "POSSIBLE_MATCH" || matches.some(m => m?.category === "TERRORIST_LIST");
  const pepMatch = result?.pepStatus === "POSSIBLE_MATCH" || matches.some(m => ["PEP", "PEP_FAMILY", "PEP_ASSOCIATE"].includes(m?.category));
  const ropMatch = localCheck?.status === "POSSIBLE_MATCH";
  const skippedOnlineTerror = ropMatch || result?.lookupSource === "ROP_AND_AI";
  const ropClear = !ropMatch && localCheck?.status === "NO_MATCH_FOUND";
  const pepClear = !pepMatch && result?.pepStatus === "NO_MATCH_FOUND";
  const onlineClear = !skippedOnlineTerror && !terroristMatch && result?.terrorismStatus === "NO_MATCH_FOUND";
  const possibleMatch = terroristMatch || pepMatch || ropMatch;
  const check = (title, match, clear, detail) => ({ title, label: match ? "Potential match" : clear ? "Cleared" : "",
    kind: match ? "match" : clear ? "clear" : "neutral", detail });
  const scope = ropClear && pepClear ? "ROP list and PEP search" : ropClear ? "ROP list" : pepClear ? "PEP search" : onlineClear ? "online terrorist search" : "";
  return {
    onlineResultsAvailable: pepMatch || pepClear || (!skippedOnlineTerror && (terroristMatch || onlineClear)),
    kind: possibleMatch ? "match" : scope ? "clear" : "neutral",
    label: possibleMatch ? "Potential match — review required" : scope ? `Cleared — ${scope}` : "Search results",
    summary: possibleMatch ? "Review the candidate details and supporting sources to verify identity."
      : scope ? `No potential match was found in the ${scope}. Results for the other checks are shown separately.`
      : "",
    checks: [
      check("Uploaded ROP terrorist list", ropMatch, ropClear, localCheck?.status === "NOT_UPLOADED" ? "No uploaded list is available." : "Name check against the uploaded list."),
      check("PEP search", pepMatch, pepClear, "Public roles and evidenced family or close associate connections."),
      skippedOnlineTerror ? { title: "Additional online terrorist search", label: "", kind: "neutral", detail: "" }
        : check("Additional online terrorist search", terroristMatch, onlineClear, "Online UN and Singapore sources; separate from the uploaded ROP result."),
    ].filter(item => item.kind !== "neutral"),
  };
};
export const categoryLabel = (category) => ({ TERRORIST_LIST: "Terrorist List", PEP: "PEP", PEP_FAMILY: "PEP family member", PEP_ASSOCIATE: "PEP close associate" }[category] || "Possible match");
export const safeScreeningSource = (value) => {
  try { const u = new URL(value); return u.protocol === "https:" && !u.username && !u.password && (!u.port || u.port === "443")
    && u.hostname.includes(".") && !u.hostname.endsWith(".local") && !/^[\d.]+$/.test(u.hostname); } catch { return false; }
};
